<template>
    <div class="timeline-page">
        <h1>{{ trans('pages.timeline.title') }}</h1>
        <date-input
            class="date-input"
            name="timelineDate"
            :placeholder="trans('pages.timeline.date_placeholder')"
            v-model="selectedDate" />
        <template v-if="fetchingStatus !== 'running'">
            <p v-if="!workIntervals && !processedProductIntervals">
                {{ trans('pages.timeline.no_timeline') }}
            </p>
            <timeline
                :label="trans('pages.timeline.based_on_clock')"
                v-if="workIntervals"
                :intervals="workIntervals"></timeline>
            <product-timeline
                isProductTimes
                v-if="processedProductIntervals"
                :intervals="processedProductIntervals"
                :workIntervals="workIntervals"
                :start="absoluteStart"
                :end="absoluteEnd"
                :label="trans('pages.timeline.based_on_workstation')"></product-timeline>
            <div v-if="workIntervals" class="summary">
                <p class="summary-title">{{ trans('pages.timeline.summary.title') }}</p>
                <div>
                    <p class="line">
                        {{ trans('pages.timeline.summary.time_worked') }}
                        <span class="bold">{{ timeWorked }}</span>
                    </p>
                    <p class="line">
                        {{ trans('pages.timeline.summary.pauses') }}
                        <span class="bold">{{ timePaused }}</span>
                    </p>
                </div>
                <div v-if="workIntervals && processedProductIntervals">
                    <p class="line">
                        {{ trans('pages.timeline.summary.productive_time_work') }}
                        <span class="bold">{{ timeProductive }}</span>
                    </p>
                    <p class="line">
                        {{ trans('pages.timeline.summary.productive_time_pause') }}
                        <span class="bold">{{ timeProductivePauses }}</span>
                    </p>
                    <p class="line">
                        {{ trans('pages.timeline.summary.inactive_time') }}
                        <span class="bold">{{ timeInactive }}</span>
                    </p>
                </div>
            </div>
        </template>
        <template v-else>
            <spinner :cover="false"></spinner>
        </template>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import Timeline from '../components/timeline/Timeline';
import ProductTimeline from '../components/timeline/ProductTimeline';
import DateInput from '../components/DateInput';
import Spinner from '../components/loader/Loader';
import { roundWithDecimal } from '../helpers/GeneralHelper';
import moment from 'moment';
import localization from '../mixins/localization';

export default {
    props: {},
    mixins: [localization],
    computed: {
        ...mapGetters({
            workTimes: 'timeline/getWorkTimes',
            productTimes: 'timeline/getProductTimes',
            fetchingStatus: 'timeline/getFetchingStatus',
        }),
        selectedDate: {
            get() {
                return this.$store.getters['timeline/getSelectedDate'];
            },
            set(value) {
                this.$store.commit('timeline/setSelectedDate', value);
            },
        },
        absoluteStart() {
            if (!this.workIntervals) {
                return null;
            }

            return moment(this.workIntervals[0].start);
        },
        absoluteEnd() {
            if (!this.workIntervals) {
                return null;
            }

            return moment(
                this.workIntervals[this.workIntervals.length - 1].end
            );
        },
        productIntervals() {
            if (!this.productTimes || this.productTimes.length == 0) {
                return;
            }

            let intervals = this.productTimes.sort(
                (a, b) => new Date(a.start) - new Date(b.start)
            );

            return intervals.map((interval) => {
                return {
                    start: moment(interval.start),
                    end: moment(interval.end),
                    type: interval.type,
                    idealMinutes: interval.idealMinutes,
                };
            });
        },
        processedProductIntervals() {
            if (!this.productIntervals || this.productIntervals.length == 0) {
                return;
            }

            // Merge productIntervals witch workIntervals
            let intervals = [];

            // Add 'break' before first interval if it isn't the same
            if (!this.absoluteStart.isSame(this.productIntervals[0].start)) {
                intervals.push({
                    start: this.absoluteStart,
                    end: this.productIntervals[0].start,
                    type: 'pause',
                });
            }

            // Add all product intervals with pauses inbetween if necessary
            this.productIntervals.forEach((interval) => {
                if (
                    intervals.length > 0 &&
                    !this.lastElementOfArray(intervals).end.isSame(
                        interval.start
                    )
                ) {
                    intervals.push({
                        start: this.lastElementOfArray(intervals).end,
                        end: interval.start,
                        type: 'pause',
                    });
                }

                intervals.push(interval);
            });

            // Add 'break' after last interval if it isn't the same
            if (
                !this.absoluteEnd.isSame(
                    this.lastElementOfArray(this.productIntervals).end
                )
            ) {
                intervals.push({
                    start: this.lastElementOfArray(this.productIntervals).end,
                    end: this.absoluteEnd,
                    type: 'pause',
                });
            }

            return intervals;
        },
        workIntervals() {
            if (!this.workTimes || this.workTimes.length == 0) {
                return;
            }

            let sortedWorkTimes = this.workTimes.sort(
                (a, b) => new Date(a.time) - new Date(b.time)
            );

            let intervals = sortedWorkTimes.map(
                (interval, index, sortedWorkTimes) => {
                    if (index + 1 == sortedWorkTimes.length) {
                        return;
                    }

                    return {
                        start: moment(interval.time),
                        end: moment(sortedWorkTimes[index + 1].time),
                        type: this.getIntervalType(
                            interval,
                            sortedWorkTimes[index + 1]
                        ),
                    };
                }
            );

            // remove last element because it is not needed and undefined
            intervals.pop();

            return intervals;
        },
        timeWorked() {
            if (!this.workIntervals || this.workIntervals.length == 0) {
                return;
            }

            const intervals = this.workIntervals.filter(
                (interval) => interval.type === 'day'
            );

            const totalDurationInMinutes = Math.floor(
                intervals.reduce((sum, interval) => {
                    return (sum += this.getIntervalDuration(interval));
                }, 0) / 60
            );

            return this.formatTime(totalDurationInMinutes);
        },
        timePaused() {
            if (!this.workIntervals || this.workIntervals.length == 0) {
                return;
            }

            const intervals = this.workIntervals.filter(
                (interval) => interval.type === 'pause'
            );

            const totalDurationInMinutes = Math.floor(
                intervals.reduce((sum, interval) => {
                    return (sum += this.getIntervalDuration(interval));
                }, 0) / 60
            );

            return this.formatTime(totalDurationInMinutes);
        },
        timeProductive() {
            if (!this.workIntervals || this.workIntervals.length == 0) {
                return;
            }

            // collect all intervals that overlap working hours. Start and end seperate
            let startOverlapPairs = [];
            let endOverlapPairs = [];
            let fullOverlapPairs = [];

            // Get working intervals
            const workIntervals = this.workIntervals.filter(
                (interval) => interval.type === 'day'
            );

            // Collect overlapping pairs
            this.productIntervals.forEach((interval) => {
                workIntervals.forEach((workInterval) => {
                    if (
                        interval.start.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        startOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        interval.end.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        endOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        // check if there is a full overlap
                        workInterval.start.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        ) &&
                        workInterval.end.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        )
                    ) {
                        fullOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    }
                });
            });

            // Get duration for all intervals remaining, check end time
            const startOverlapDuration = startOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.end.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end,
                            null,
                            []
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.productInterval.start,
                            end: overlapPair.workInterval.end,
                        }));
                    }
                },
                0
            );

            const endOverlapDuration = endOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.start.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end,
                            null,
                            []
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.workInterval.start,
                            end: overlapPair.productInterval.end,
                        }));
                    }
                },
                0
            );

            const fullOverlapDuration = fullOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    return (sum += this.getIntervalDuration({
                        start: overlapPair.workInterval.start,
                        end: overlapPair.workInterval.end,
                    }));
                },
                0
            );

            const totalWorkTimeInMinutes =
                workIntervals.reduce((sum, interval) => {
                    return (sum += this.getIntervalDuration(interval));
                }, 0) / 60;

            const totalDurationInMinutes =
                (startOverlapDuration +
                    endOverlapDuration +
                    fullOverlapDuration) /
                60;

            return this.formatTime(
                totalDurationInMinutes,
                (totalDurationInMinutes / totalWorkTimeInMinutes) * 100
            );
        },
        timeProductivePauses() {
            if (!this.workIntervals || this.workIntervals.length == 0) {
                return;
            }

            // collect all intervals that overlap pauses. Start and end seperate
            let startOverlapPairs = [];
            let endOverlapPairs = [];
            let fullOverlapPairs = [];

            // Get working intervals
            const workIntervals = this.workIntervals.filter(
                (interval) => interval.type === 'pause'
            );

            // Collect overlapping pairs
            this.productIntervals.forEach((interval) => {
                workIntervals.forEach((workInterval) => {
                    if (
                        interval.start.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        startOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        interval.end.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        endOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        // check if there is a full overlap
                        workInterval.start.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        ) &&
                        workInterval.end.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        )
                    ) {
                        fullOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    }
                });
            });

            // Get duration for all intervals remaining, check end time
            const startOverlapDuration = startOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.end.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end,
                            null,
                            []
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.productInterval.start,
                            end: overlapPair.workInterval.end,
                        }));
                    }
                },
                0
            );

            const endOverlapDuration = endOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.start.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end,
                            null,
                            []
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.workInterval.start,
                            end: overlapPair.productInterval.end,
                        }));
                    }
                },
                0
            );

            const fullOverlapDuration = fullOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    return (sum += this.getIntervalDuration({
                        start: overlapPair.workInterval.start,
                        end: overlapPair.workInterval.end,
                    }));
                },
                0
            );

            const totalWorkTimeInMinutes =
                workIntervals.reduce((sum, interval) => {
                    return (sum += this.getIntervalDuration(interval));
                }, 0) / 60;

            const totalDurationInMinutes =
                (startOverlapDuration +
                    endOverlapDuration +
                    fullOverlapDuration) /
                60;

            return this.formatTime(
                totalDurationInMinutes,
                (totalDurationInMinutes / totalWorkTimeInMinutes) * 100
            );
        },
        timeInactive() {
            if (!this.workIntervals || this.workIntervals.length == 0) {
                return;
            }

            // collect all intervals that overlap working hours. Start and end seperate
            let startOverlapPairs = [];
            let endOverlapPairs = [];
            let fullOverlapPairs = [];

            // Get working intervals
            const workIntervals = this.workIntervals.filter(
                (interval) => interval.type === 'day'
            );

            // Get inactive intervals
            const productIntervals = this.processedProductIntervals.filter(
                (interval) => interval.type === 'pause'
            );

            // Collect overlapping pairs
            productIntervals.forEach((interval) => {
                workIntervals.forEach((workInterval) => {
                    if (
                        interval.start.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        startOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        interval.end.isBetween(
                            workInterval.start,
                            workInterval.end,
                            null,
                            []
                        )
                    ) {
                        endOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    } else if (
                        // check if there is a full overlap
                        workInterval.start.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        ) &&
                        workInterval.end.isBetween(
                            interval.start,
                            interval.end,
                            null,
                            []
                        )
                    ) {
                        fullOverlapPairs.push({
                            workInterval,
                            productInterval: interval,
                        });
                    }
                });
            });

            // Get duration for all intervals remaining, check end time
            const startOverlapDuration = startOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.end.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.productInterval.start,
                            end: overlapPair.workInterval.end,
                        }));
                    }
                },
                0
            );

            const endOverlapDuration = endOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    if (
                        overlapPair.productInterval.start.isBetween(
                            overlapPair.workInterval.start,
                            overlapPair.workInterval.end
                        )
                    ) {
                        return (sum += this.getIntervalDuration(
                            overlapPair.productInterval
                        ));
                    } else {
                        return (sum += this.getIntervalDuration({
                            start: overlapPair.workInterval.start,
                            end: overlapPair.productInterval.end,
                        }));
                    }
                },
                0
            );

            const fullOverlapDuration = fullOverlapPairs.reduce(
                (sum, overlapPair) => {
                    // if productInterval end is inside workInterval, calculate duration of productInterval
                    return (sum += this.getIntervalDuration({
                        start: overlapPair.workInterval.start,
                        end: overlapPair.workInterval.end,
                    }));
                },
                0
            );

            const totalWorkTimeInMinutes =
                workIntervals.reduce((sum, interval) => {
                    return (sum += this.getIntervalDuration(interval));
                }, 0) / 60;

            const totalDurationInMinutes =
                (startOverlapDuration +
                    endOverlapDuration +
                    fullOverlapDuration) /
                60;

            return this.formatTime(
                totalDurationInMinutes,
                (totalDurationInMinutes / totalWorkTimeInMinutes) * 100
            );
        },
    },
    methods: {
        ...mapActions({
            fetchDetails: 'timeline/fetchTimelineDetails',
        }),
        getIntervalType(time, nextTime) {
            if (time.type.includes('Start')) {
                return time.type.includes('day') ? 'day' : 'pause';
            } else if (time.type.includes('End')) {
                if (time.type === 'pauseEnd') {
                    return 'day';
                }

                if (nextTime.type === 'pauseStart') {
                    return 'day';
                } else if (nextTime.type === 'pauseEnd') {
                    return 'pause';
                } else {
                    return 'day';
                }
            }
        },
        fetch() {
            this.fetchDetails(this.$route.params.id);
        },
        getIntervalDuration(interval) {
            return interval.end.diff(interval.start, 'seconds');
        },
        formatTime(totalInMinutes, percentage) {
            const hours =
                Math.floor(totalInMinutes / 60) < 10
                    ? '0' + Math.floor(totalInMinutes / 60)
                    : Math.floor(totalInMinutes / 60);
            const seconds =
                Math.floor(totalInMinutes % 60) < 10
                    ? '0' + Math.floor(totalInMinutes % 60)
                    : Math.floor(totalInMinutes % 60);

            let percentageResponse = '';

            if (percentage) {
                percentageResponse = ` (${Math.floor(percentage)}%)`;
            }

            return hours + ':' + seconds + percentageResponse;
        },
        lastElementOfArray(array) {
            return array[array.length - 1];
        },
    },
    watch: {
        selectedDate() {
            this.fetch();
        },
    },
    components: {
        Timeline,
        ProductTimeline,
        DateInput,
        Spinner,
    },
    mounted() {
        this.fetch();
    },
};
</script>
